import { envConfig } from '#constants/envConfig';

/** Runtime iOS bridge, never the user agent: Safari must retain web reCAPTCHA. */
export function isIOSApp(): boolean {
  if (!envConfig.IS_NATIVE || typeof window === 'undefined') return false;
  const nativeWindow = window as unknown as {
    Capacitor?: { getPlatform?: () => string };
    webkit?: { messageHandlers?: { bridge?: unknown } };
  };
  return (
    nativeWindow.Capacitor?.getPlatform?.() === 'ios' ||
    nativeWindow.webkit?.messageHandlers?.bridge !== undefined
  );
}

/**
 * Firebase App Check для нативной оболочки: токен, которым платформа ручается, что запрос идёт
 * из подлинной сборки нашего приложения (на Android за этим стоит Play Integrity).
 *
 * Зачем он вместо капчи. reCAPTCHA v3 в WebView не работает в принципе: страница открыта с
 * `https://localhost`, истории браузера и репутации домена нет, и живому человеку стабильно
 * выдаётся score 0.1. 18.08.2026 из-за этого регистрация из приложения не работала ни у кого —
 * один пользователь нажал «Зарегистрироваться» восемь раз и так и не завёл аккаунт. Шлюз
 * принимает этот токен заголовком `tm-app-check` и в таком случае капчу не спрашивает вовсе.
 *
 * Всё внутри закрыто `envConfig.IS_NATIVE` — константой времени сборки. В веб-сборке она
 * сворачивается в `false`, динамический импорт Capacitor вырезается, и веб-бандл о нём не знает.
 */

type AppCheckPlugin = {
  getToken(): Promise<{ token: string | null }>;
};

// undefined = плагин ещё не запрашивали; null = запрашивали, его нет (веб или старая сборка).
let cached: AppCheckPlugin | null | undefined;
let loading: Promise<void> | null = null;

/**
 * Загружает прокси плагина ровно один раз и кладёт в `cached`. Возвращает `Promise<void>` и
 * НИКОГДА сам прокси: `registerPlugin` отдаёт Proxy, который перехватывает любое обращение к
 * свойству, включая `.then`. Стоит такому прокси стать значением, которым резолвится промис, —
 * рантайм примет его за thenable, вызовет несуществующий нативный метод `then` и уронит вызов
 * ещё до моста (ровно эта ловушка описана в `deviceSettings.ts`).
 */
function ensurePlugin(): Promise<void> {
  if (!envConfig.IS_NATIVE) {
    cached = null;
    return Promise.resolve();
  }
  if (cached !== undefined) {
    return Promise.resolve();
  }
  if (!loading) {
    loading = (async () => {
      try {
        const { registerPlugin } = await import('@capacitor/core');
        cached = registerPlugin<AppCheckPlugin>('AppCheck');
      } catch {
        cached = null;
      }
    })();
  }
  return loading;
}

/**
 * Сколько операция с капчей ждёт токен, прежде чем уйти без него.
 *
 * Обмен Play Integrity → Firebase на Mi 10T Pro по Wi-Fi занимал 2–4 с (замер 15.09.2026,
 * отладочная сборка; на ней он всегда кончается 403, но по времени это тот же обмен). 8 с – это
 * вдвое больше худшего замера: запас на медленный телефон и мобильную сеть. Дольше держать кнопку
 * входа в ожидании нет смысла: обмен, не уложившийся в 8 с, скорее всего завис.
 *
 * На iOS отсутствие токена означает отказ защищённой операции, включая dev: сервер не
 * переходит к reCAPTCHA. Android сохраняет прежний fallback. Поэтому ждём щедро, но не
 * держим кнопку бесконечно при недоступной аттестации.
 *
 * Обычно ждать не приходится вовсе: токен запрашивается при старте (`prewarmAppCheckToken`), и
 * ко входу SDK отдаёт его из своего кеша (живёт час).
 */
export const APP_CHECK_WAIT_MS = 8_000;

// Один запрос к плагину на всех: прогрев при старте и операция с капчей, пришедшая, пока он идёт,
// ждут один и тот же ответ. Иначе вторая копия могла бы запустить ещё одну аттестацию Play
// Integrity, а Google их жёстко ограничивает.
let inFlight: Promise<string | null> | null = null;

function requestToken(): Promise<string | null> {
  if (!inFlight) {
    inFlight = (async () => {
      await ensurePlugin();
      if (!cached) {
        return null;
      }

      try {
        const { token } = await cached.getToken();
        return token ?? null;
      } catch {
        // Токены и нативные ошибки не отправляем в Sentry. Решение об отказе принимает шлюз;
        // на iOS защищённая операция без токена не проходит.
        return null;
      }
    })().finally(() => {
      // Ответ не запоминаем: удачный токен кеширует SDK (и сам обновит его, когда истечёт), а
      // неудачный стоит спросить заново при следующей операции с капчей.
      inFlight = null;
    });
  }

  return inFlight;
}

/**
 * Начать получать токен заранее, ничего не дожидаясь. Зовётся один раз при старте приложения,
 * после первой отрисовки, чтобы к моменту входа или регистрации токен уже лежал в кеше SDK.
 *
 * Обращений к Play Integrity от этого не прибавляется: раньше аттестацию запускал первый же
 * GraphQL-запрос на старте, теперь – этот вызов, а сами запросы токен больше не просят.
 */
export function prewarmAppCheckToken(): void {
  if (!envConfig.IS_NATIVE) {
    return;
  }

  void requestToken();
}

/**
 * @returns токен App Check либо `null` — в вебе, в сборке без плагина, на устройстве, где
 * аттестация недоступна, и если токен не пришёл за `timeoutMs`. iOS-шлюз отклонит защищённую
 * операцию, Android сохраняет fallback к капче. Запрос, не уложившийся в срок, не отменяется: его ответ
 * осядет в кеше SDK и достанется следующей операции.
 *
 * Нужен только операциям с капчей (см. `captchaOperations.ts`), остальным запросам шлюз токен не
 * проверяет, и ждать его им нельзя: аттестация идёт секунды, и столько же стояла пустой лента.
 */
export async function getAppCheckToken(timeoutMs = APP_CHECK_WAIT_MS): Promise<string | null> {
  if (!envConfig.IS_NATIVE) {
    return null;
  }

  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<null>((resolve) => {
    timer = setTimeout(() => resolve(null), timeoutMs);
  });

  try {
    return await Promise.race([requestToken(), timeout]);
  } finally {
    clearTimeout(timer);
  }
}
